import type { DocumentMarker, Marker } from '@frameio/graphql-client/generated';

/** Stamped on marker-upload comment bodies */
const USER_ID_FOR_MARKER_UPLOAD = 'Frame.io User ID:';

/** Stamped on comment-marker sync marker bodies */
export const COMMENT_ID_FOR_MARKER_SYNC = 'Frame.io Comment ID:';

export type HostMarkers = DocumentMarker | Marker;

/** Input for one `createComment` call derived from a host marker. */
export type CommentFromMarkerPayload = {
  duration?: number | null;
  initialText: string;
  /** Host marker id (Premiere guid or AE `ae-key:N`) used for upload linking. */
  markerId?: string | null;
  timestampMicroseconds: number;
};

/**
 * Whether `text` already contains this session user's marker-upload stamp
 * (`Frame.io User ID:` + user id), so we should not treat it as a fresh host
 * marker.
 */
export function commentTextIncludesMarkerExportStamp(
  text: string | null | undefined,
  currentUserId?: string | null
): boolean {
  if (!text || !currentUserId) {
    return false;
  }

  return text.includes(`${USER_ID_FOR_MARKER_UPLOAD} ${currentUserId}`);
}

/** Add a user ID stamp to the comment text. This helps later to identify frame
 * comments created via marker upload when marker-sync is enabled: prevents
 * creating duplicate markers in user's document (original uploaded marker in
 * document + synced marker from frame comment)
 */
export function stampMarkerExportCommentBody(
  baseText: string,
  userId: string
): string {
  if (
    baseText.includes(USER_ID_FOR_MARKER_UPLOAD) ||
    baseText.includes(COMMENT_ID_FOR_MARKER_SYNC)
  ) {
    return baseText;
  }

  return `${baseText}\n\n${USER_ID_FOR_MARKER_UPLOAD} ${userId}`;
}

/**
 * Shape comment text from a marker.
 */
export function markerInitialText(marker: {
  name?: string | null;
  comments?: string | null;
}): string {
  const { name, comments } = marker;
  return name && comments
    ? `${name}\n\n${comments}`
    : `${name || comments || ''}`;
}

/**
 * Timeline markers eligible for Frame.io export comments (`upload`/`export`):
 * not deleted, name or comments present, not already uploaded for this asset,
 * and marker text does not carry this user's marker-upload stamp yet. Range
 * filtering (Premiere in/out, AE work area) is applied separately by
 * `useExportMarkersAsComments` after this.
 */
export function filterDocumentMarkers(
  markers: Array<HostMarkers>,
  currentUserId: string | null | undefined,
  options?: {
    /**
     * Comment ids already uploaded for this asset (host registry). When
     * present, only skip markers whose `frameCommentId` is in the registry —
     * allows re-exporting the same sequence to a new asset when markers still
     * carry links from a prior upload. When absent (registry unsupported, or
     * its fetch failed), fall back to skipping any marker that already has a
     * `frameCommentId`.
     */
    registeredUploadCommentIds?: ReadonlySet<string>;
  }
): HostMarkers[] {
  return markers.filter((m) => {
    if (
      !m ||
      m.deleted ||
      !(m.name || m.comments) ||
      commentTextIncludesMarkerExportStamp(m.comments, currentUserId)
    ) {
      return false;
    }

    if (!m.frameCommentId) {
      return true;
    }

    if (options?.registeredUploadCommentIds) {
      return !options.registeredUploadCommentIds.has(m.frameCommentId);
    }

    return false;
  });
}
